import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { makeStyles } from '@mui/styles';
import MultiSelection from '@/components/UI/MultiSelection/MultiSelection';
import useApiRequest from '@/hooks/useApiRequest';
import useGlobalSpinnerContext from '@/hooks/useGlobalSpinner';
import useToastContext from '@/hooks/useToastContext';
import campaignServices from '@/services/campaignServices';
import { AffectedVehicles } from '@/types';
import { ThemeOptions } from '@/utils/componentStyles';

interface SplitSubCampaignPopupProps {
  subCRId: string;
  affectedVehicles: AffectedVehicles;
  reloadSubCR: () => void;
  closePopup: () => void;
  plant: string;
}

const useStyles = makeStyles(({ cartTheme }: ThemeOptions) => ({
  titleRoot: {
    borderBottom: `1px solid ${cartTheme.commonGrey}`,
  },
  buttonsRoot: {
    borderTop: `1px solid ${cartTheme.commonGrey}`,
  },
  dialogPaper: {
    minWidth: '30%',
  },
}));

const SplitSubCampaignPopup: React.FC<SplitSubCampaignPopupProps> = (props) => {
  const { subCRId, affectedVehicles, reloadSubCR, closePopup, plant } = props;
  const { t } = useTranslation();
  const classes = useStyles();
  const [step, setStep] = useState(1);
  const [carlinesAvailable, setCarlinesAvailable] = useState<string[]>([]);
  const [carlinesSelected, setCarlinesSelected] = useState<string[]>([]);
  const [countryVariantsAvailable, setCountryVariantsAvailable] = useState<string[]>([]);
  const [countryVariantsSelected, setCountryVariantsSelected] = useState<string[]>([]);
  const { addToast } = useToastContext();
  const { loading, setLoading } = useGlobalSpinnerContext();
  const fetchSplitSubcr = useApiRequest(campaignServices.splitSubCampaign);
  const stepsAvailable = countryVariantsAvailable.length > 0 ? 2 : 1;

  const handleChipDelete = (chipCVToDelete: string) => {
    setCountryVariantsSelected(countryVariantsSelected.filter((chip) => chip !== chipCVToDelete));
  };

  const onSplitSubCR = () => {
    if (carlinesAvailable.length === carlinesSelected.length) {
      addToast(t('errors.atLeastOneMSSplit'), 'Warning');

      return;
    };
    setLoading(true);
    const agrupedCarlines = carlinesSelected.reduce((acc, carline) => {
      const [modelSerie, typeVersion] = carline.split('-');
      const carlineGroup = acc[modelSerie] || [];

      return { ...acc, [modelSerie]: [...carlineGroup, typeVersion] };
    }, {});

    const data = {
      modelSeriesTypeVersions: agrupedCarlines,
      countryVariants: countryVariantsSelected,
    };

    fetchSplitSubcr(subCRId, data)
      .then((newSubCRId) => {
        reloadSubCR();
        addToast(t('alert.subCRSplitted', { newSubCRId }), 'Success');
        closePopup();
      })
      .catch((error) => addToast(error?.message, 'ERROR'))
      .finally(() => {
        setLoading(false);
      });
  };

  const onNextStepClicked = () => {
    if (carlinesAvailable.length === carlinesSelected.length) {
      addToast(t('errors.atLeastOneMSSplit'), 'Warning');
    } else {
      setStep(2);
    }
  };

  useEffect(() => {
    const filterResults = (car: string[]): string[] => Array.from(new Set(car));
    const carlines: string[] = filterResults(affectedVehicles.affectedSeries.reduce((acc: string[], modelSerie) => acc.concat(modelSerie.typeVersions.reduce((acc2, typeVersion) => acc2.concat(`${modelSerie.modelSerie}-${typeVersion.typeVersion}`), acc), []), []));
    const countryVariants: string[] = filterResults(affectedVehicles.volumes.reduce((acc: string[], volume) => volume.plant === plant ? acc.concat(volume.limitationVolumeManualEntry.countryVariantsSelecteds) : acc, []));

    if (carlines.length <= 1) {
      addToast(t('errors.atLeastTwoMSSplit'), 'Warning');
      closePopup();
    }

    setCarlinesAvailable(carlines);
    setCountryVariantsAvailable(countryVariants);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [affectedVehicles]);

  return (
    <Dialog
      open
      id="newCR-dialog"
      classes={{ paper: classes.dialogPaper }}
      maxWidth="md"
      onClose={closePopup}
      aria-labelledby="newCR-dialog-title"
      disableEscapeKeyDown={loading}
      data-testid="new-cr-dialog"
    >
      <DialogTitle classes={{ root: classes.titleRoot }} id="newCR-dialog-title">{t('campaign.splitSubCampaign')}</DialogTitle>
      <DialogContent>
        <div className="w-100">
          <MultiSelection
            id="carlines"
            formControlClass="w-100"
            label={t('affectedVehicles.modelSeries')}
            options={carlinesAvailable}
            value={carlinesSelected}
            handleChange={(ev) => setCarlinesSelected(ev.target.value as unknown as string[])}
            isModelSeries
            disabled={step !== 1}
            invalid={false}
            fieldPath={[]}
          />
        </div>
        {step === 2 && (
          <>
            <br />
            <div className="w-100">
              <MultiSelection
                id="countryVariants"
                formControlClass="w-100"
                label={t('affectedVehicles.COUNTRY_VARIANTS')}
                options={countryVariantsAvailable}
                value={countryVariantsSelected}
                handleChange={(ev) => setCountryVariantsSelected(ev.target.value as unknown as string[])}
                handleDeleteItem={handleChipDelete}
                isModelSeries
                disabled={false}
                invalid={false}
                fieldPath={[]}
              />
            </div>
          </>
        )}
      </DialogContent>
      <DialogActions classes={{ root: classes.buttonsRoot }}>
        <Button
          id="split-subcr-next-btn"
          data-testid="split-subcr-next-btn"
          disabled={carlinesSelected.length === 0}
          variant="contained"
          color="primary"
          onClick={(step === stepsAvailable) ? onSplitSubCR : onNextStepClicked}
        >
          {t(`dialog.${step === stepsAvailable ? 'split' : 'next'}`)}
        </Button>

        <Button
          id="back-cancel-btn"
          data-testid="back-cancel-btn"
          disabled={loading}
          color="primary"
          onClick={(step === 2) ? () => setStep(1) : closePopup}
        >
          {t(`dialog.${(step === 2) ? 'back' : 'cancel'}`)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SplitSubCampaignPopup;
